import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";
import { browserStorage } from "@deskpatrol/core";

export const THEME_STORAGE_KEY = "deskpatrol-theme";
export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const ThemeContext = createContext<{
  theme: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemeMode) => void;
} | null>(null);

export const brandTokens = {
  colorInk: "hsl(var(--foreground))",
  colorMuted: "hsl(var(--muted-foreground))",
  colorAccent: "hsl(var(--primary))",
  colorHighlight: "hsl(var(--accent))",
  colorCanvas: "hsl(var(--background))",
} as const;

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function getStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const stored = browserStorage.local.getItem(THEME_STORAGE_KEY);
  return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
}

export function resolveTheme(theme: ThemeMode): ResolvedTheme {
  return theme === "system" ? getSystemTheme() : theme;
}

export function applyThemeToDocument(theme: ThemeMode) {
  if (typeof document === "undefined") return;
  const resolvedTheme = resolveTheme(theme);
  const root = document.documentElement;
  root.classList.toggle("dark", resolvedTheme === "dark");
  root.dataset.theme = theme;
  root.style.colorScheme = resolvedTheme;
}

export function initializeTheme(theme = getStoredTheme()) {
  applyThemeToDocument(theme);
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const [theme, setThemeState] = useState<ThemeMode>(() => getStoredTheme());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(getStoredTheme()));

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const syncTheme = () => {
      const nextResolved = resolveTheme(theme);
      setResolvedTheme(nextResolved);
      applyThemeToDocument(theme);
    };
    syncTheme();
    media.addEventListener("change", syncTheme);
    return () => media.removeEventListener("change", syncTheme);
  }, [theme]);

  const value = useMemo(() => ({
    theme,
    resolvedTheme,
    setTheme: (nextTheme: ThemeMode) => {
      browserStorage.local.setItem(THEME_STORAGE_KEY, nextTheme);
      setThemeState(nextTheme);
      setResolvedTheme(resolveTheme(nextTheme));
      applyThemeToDocument(nextTheme);
    },
  }), [resolvedTheme, theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme 必须在 ThemeProvider 内使用");
  return context;
}
